import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Button,
  Snackbar,
  Checkbox,
  FormHelperText,
  FormControl,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../AuthContext";
import useAuthenticatedFetch from "../hooks/useAuthenticatedFetch";
import { useTranslation } from "react-i18next";

const ShiurEditor = ({ onIsDirtyChange }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rooms, setRooms] = useState([]);
  const [rabanim, setRabanim] = useState([]);
  const [shiurim, setShiurim] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState([]);
  const [hasFetched, setHasFetched] = useState(false); // Nouvel état pour empêcher les appels multiples
  const [errorMessage, setErrorMessage] = useState("");
  const [community, setCommunity] = useState(null);
  const [fontSize, setFontSize] = useState("16");
  const fetchWithAuth = useAuthenticatedFetch();
  const daysOfWeek = [
    { value: "monday", label: t("shiur.daysOfWeek.monday") },
    { value: "tuesday", label: t("shiur.daysOfWeek.tuesday") },
    { value: "wednesday", label: t("shiur.daysOfWeek.wednesday") },
    { value: "thursday", label: t("shiur.daysOfWeek.thursday") },
    { value: "friday", label: t("shiur.daysOfWeek.friday") },
    { value: "saturday", label: t("shiur.daysOfWeek.saturday") },
    { value: "sunday", label: t("shiur.daysOfWeek.sunday") },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (hasFetched) return; // Empêche l'appel si une tentative a déjà été faite

      try {
        // Charger les données de la communauté
        const communityResponse = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/community/getCommunityById/${user.communityId}`
        );
        if (!communityResponse.ok) throw new Error(t("shiur.errors.loadCommunity"));
        const communityData = await communityResponse.json();
        setCommunity(communityData);

        // Charger la taille de police initiale pour "Shiur"
        if (communityData.fontSizeSetting) {
          const settings = JSON.parse(communityData.fontSizeSetting);
          const shiurSetting = settings.find(setting => setting.name === "Shiur");
          if (shiurSetting) {
            setFontSize(shiurSetting.size);
          }
        }

        const roomsResponse = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/room/getRooms?communityId=${user.communityId}`
        );
        const roomsData = await roomsResponse.json();
        setRooms(roomsData);

        const rabanimResponse = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/rav/getRabanim?communityId=${user.communityId}`
        );
        const rabanimData = await rabanimResponse.json();
        setRabanim(rabanimData);

        const shiurResponse = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/shiur/getShiurim?communityId=${user.communityId}`
        );
        const shiurData = await shiurResponse.json();

        // Convertir les données groupées par jour en tableau plat pour l'édition
        const formattedShiurim = [];
        if (typeof shiurData === 'object' && !Array.isArray(shiurData)) {
          // Nouveau format groupé par jour
          Object.keys(shiurData).forEach(day => {
            shiurData[day].forEach(course => {
              formattedShiurim.push({
                ...course,
                day: [day] // Conversion en tableau pour l'éditeur
              });
            });
          });
        } else {
          // Ancien format (tableau plat) - pour compatibilité
          formattedShiurim.push(...shiurData.map((shiur) => ({
            ...shiur,
            day: shiur.day ? shiur.day.split(",") : [], // Conversion de day en tableau
          })));
        }

        setShiurim(formattedShiurim);
        setErrors(new Array(formattedShiurim.length).fill(false)); // Initialiser les erreurs
        setHasFetched(true);
      } catch (error) {
        console.error(error.message);
        setErrorMessage(t("shiur.errors.fetchError"));
        setHasFetched(true);
      }
    };

    fetchData();
  }, [user.communityId]);

  useEffect(() => {
    if (onIsDirtyChange) {
      onIsDirtyChange(isDirty); // Notifier le parent chaque fois que isDirty change
    }
  }, [isDirty, onIsDirtyChange]);

  const handleAddRow = () => {
    setShiurim([
      ...shiurim,
      { roomId: "", time: "", day: [], subject: "", ravId: "",  endTime: "" },
    ]);
    setErrors([...errors, false]); // Ajouter une nouvelle ligne sans erreurs
    setIsDirty(true); 
  };

  const handleUpdateRow = (index, field, value) => {
    const updatedShiurim = [...shiurim];
    updatedShiurim[index][field] = value || null; // Remplacez les chaînes vides par `null`
    setShiurim(updatedShiurim);
    // Gestion des erreurs
    const updatedErrors = [...errors];

    if ((field === "day" && value.length > 0) || (field !== "day" && value)) {
      // Réinitialisez l'erreur pour le champ corrigé
      updatedErrors[index] = false;
    } else if (!value) {
      // Si un champ est vide, marquez-le comme une erreur
      updatedErrors[index] = true;
    }
    setIsDirty(true);
  };

  const handleRemoveRow = (index) => {
    const updatedShiurim = shiurim.filter((_, i) => i !== index);
    const updatedErrors = errors.filter((_, i) => i !== index);
    setShiurim(updatedShiurim);
    setErrors(updatedErrors);
    setIsDirty(true);
  };

  const validateFields = () => {
    const updatedErrors = shiurim.map(
      (row) => !row.ravId || !row.time || row.day.length === 0
    );
    setErrors(updatedErrors);
    return !updatedErrors.includes(true); // Retourne `false` s'il y a des erreurs
  };

  const handleSave = async () => {
    if (!validateFields()) {
      setErrorMessage(t("common.validationError")); // Message d'erreur global
      return;
    }

    try {
      // Sauvegarder la taille de police en utilisant les données existantes
      const currentSettings = community.fontSizeSetting ? JSON.parse(community.fontSizeSetting) : [];
      const updatedSettings = currentSettings.map(setting => 
        setting.name === "Shiur" 
          ? { ...setting, size: fontSize }
          : setting
      );

      if (!updatedSettings.some(setting => setting.name === "Shiur")) {
        updatedSettings.push({ name: "Shiur", size: fontSize });
      }

      await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/community/updateFontSizeSettings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            communityId: user.communityId,
            fontSizeSetting: JSON.stringify(updatedSettings),
          }),
        }
      );

      const cleanedShiurim = shiurim.map((row) => ({
        ...row,
        roomId: row.roomId || null, // Remplacez les chaînes vides par `null`
        endTime: row.endTime || "00:00", // Définit endTime par défaut à "00:00"
      }));

      await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/shiur/saveShiur`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ communityId: user.communityId, rows: cleanedShiurim }),
      });
      setSuccessMessage(t("shiur.success.save"));
      setIsDirty(false);
    } catch (error) {
      console.error(error.message);
      setErrorMessage(t("shiur.errors.saveError"));
    }
  };

  return (
    <Box sx={{ mt: 1, width: "100%" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t("shiur.title")}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={t("schedule.fontSize")}
            type="number"
            value={fontSize}
            onChange={(e) => {
              setFontSize(e.target.value);
              setIsDirty(true);
            }}
            size="small"
            sx={{ width: '120px' }}
            InputProps={{
              endAdornment: <span style={{ marginLeft: '4px' }}>px</span>
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isDirty}
          >
            {t('shiur.save')}
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        {shiurim.map((row, index) => (
          <React.Fragment key={index}>
            {/* En-tête de la ligne avec le numéro et le bouton supprimer */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, paddingTop: 1,borderTop: "1px solid #ccc", }}>
              <Typography variant="h6">
                {`${t('shiur.number')} ${index + 1}`}
              </Typography>
              <IconButton
                onClick={() => handleRemoveRow(index)}
                aria-label={t('shiur.delete')}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* Ligne avec les champs */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {/* Rav */}
              <Box sx={{ flex: '1 1', marginBottom: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', marginBottom: 1 }}>
                  {t('shiur.rav')}
                </Typography>
                <FormControl
                  fullWidth
                  error={errors[index] && !row.ravId} // Définit l'état d'erreur pour le FormControl
                >
                    <Select
                      value={row.ravId}
                      onChange={(e) => handleUpdateRow(index, "ravId", e.target.value)}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem value="" disabled>
                        {t("shiur.selectRav")}
                      </MenuItem>
                      {rabanim.map((rav) => (
                        <MenuItem key={rav.ravId} value={rav.ravId}>
                          {rav.ravName}
                        </MenuItem>
                      ))}
                    </Select>
                    {/* Affiche un message d'erreur si nécessaire */}
                    {errors[index] && !row.ravId && (
                      <FormHelperText>{t("common.required")}</FormHelperText>
                    )}
                  </FormControl>
                </Box>

              {/* Time */}
              <Box sx={{ flex: '1 1', marginBottom: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', marginBottom: 1 }}>
                  {t('shiur.time')}
                </Typography>
                <TextField
                  type="time"
                  value={row.time}
                  onChange={(e) => handleUpdateRow(index, "time", e.target.value)}
                  error={errors[index] && !row.time}
                  helperText={errors[index]  && !row.time ? t("common.required") : ""}
                  fullWidth
                />
              </Box>
              {/* Time */}
              <Box sx={{ flex: '1 1', marginBottom: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', marginBottom: 1 }}>
                  {t('shiur.endTime')}
                </Typography>
                <TextField
                  type="time"
                  value={row.endTime}
                  onChange={(e) => handleUpdateRow(index, "endTime", e.target.value)}
                  fullWidth
                />
              </Box>
              {/* Days */}
              <Box sx={{ flex: '1 1', marginBottom: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', marginBottom: 1 }}>
                  {t('shiur.days')}
                </Typography>
                <FormControl
                  fullWidth
                  error={errors[index] && row.day.length === 0} // Définit l'état d'erreur pour le FormControl
                >
                  <Select
                    multiple
                    value={row.day}
                    onChange={(e) => handleUpdateRow(index, "day", e.target.value)}
                    renderValue={(selected) =>
                      selected.map((day) => daysOfWeek.find((d) => d.value === day)?.label).join(", ")
                    }
                    fullWidth
                  >
                    {daysOfWeek.map((day) => (
                      <MenuItem key={day.value} value={day.value}>
                        <Checkbox checked={row.day.includes(day.value)} />
                        <Typography>{day.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  {/* Affiche un message d'erreur si nécessaire */}
                  {errors[index] && row.day.length === 0 && (
                    <FormHelperText>{t("common.required")}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Subject */}
              <Box sx={{ flex: '1 1 200px', marginBottom: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', marginBottom: 1 }}>
                  {t('shiur.subject')}
                </Typography>
                <TextField
                  value={row.subject}
                  onChange={(e) => handleUpdateRow(index, "subject", e.target.value)}
                  placeholder={t("shiur.subject")}
                  fullWidth
                />
              </Box>

              {/* Salle */}
              <Box sx={{ flex: '1 1', marginBottom: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', marginBottom: 1 }}>
                  {t('shiur.room')}
                </Typography>
                <Select
                  value={row.roomId}
                  onChange={(e) => handleUpdateRow(index, "roomId", e.target.value)}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="" disabled>
                    {t("shiur.selectRoom")}
                  </MenuItem>
                  {rooms.map((room) => (
                    <MenuItem key={room.roomId} value={room.roomId}>
                      {room.roomName}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </React.Fragment>
        ))}

        {/* Bouton pour ajouter une nouvelle ligne */}
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
          <IconButton onClick={handleAddRow} aria-label={t('shiur.add')} color="primary">
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={!isDirty}>
          {t("shiur.save")}
        </Button>
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage("")}
        message={errorMessage}
      />
    </Box>
  );
};

export default ShiurEditor;